import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { SectionTilesProps } from '../../utils/SectionProps';
import SectionHeader from './partials/SectionHeader';
import { FiCheck } from 'react-icons/fi';
import { GREEN, RED, PRIMARY_COLOR, WHITE, PALE_WHITE_6 } from '../../../../appearance/Colors';
import LimitedOfferBanner from '../../../../components/LimitedOfferBanner/LimitedOfferBanner';
import { Link, useLocation } from 'react-router-dom';
import { usePlans } from '../../../../hooks/usePlans';
import SinglePlanPricing from './SinglePlanPricing';
import { redirectToMobileStore } from '../../../../util/mobileStoreRedirect';

const propTypes = {
    ...SectionTilesProps.types
};

const defaultProps = {
    ...SectionTilesProps.defaults
};

const Pricing = ({
    className,
    topOuterDivider,
    bottomOuterDivider,
    topDivider,
    bottomDivider,
    hasBgColor,
    invertColor,
    pushLeft,
    ...props
}) => {
    const { plans, loading } = usePlans();
    const location = useLocation();
    const [subscriptionType, setSubscriptionType] = useState('monthly'); // 'weekly', 'monthly', or 'yearly'
    const [showOneTimeDeals, setShowOneTimeDeals] = useState(false); // Toggle for one-time deals

    // Helper function to modify Stripe payment link with promo code from URL
    const getPaymentLinkWithPromo = (originalLink) => {
        if (!originalLink) return originalLink;
        
        // Check for promo code in URL (priority: URL > localStorage)
        const urlParams = new URLSearchParams(location.search);
        const promoCodeFromUrl = urlParams.get('promocode') || urlParams.get('coupon') || urlParams.get('promo');
        const savedPromoCode = localStorage.getItem('promoCode');
        const promoCodeToUse = promoCodeFromUrl || savedPromoCode;
        
        if (!promoCodeToUse) {
            // No promo code, return original link
            return originalLink;
        }
        
        try {
            // Parse the original Stripe payment link
            const paymentUrl = new URL(originalLink);
            
            // Remove existing prefilled_promo_code parameter (to override pre-applied one)
            paymentUrl.searchParams.delete('prefilled_promo_code');
            
            // Replace prefilled_promo_code with the new promo code from URL/localStorage
            // This ensures the new promo code is used instead of the pre-applied one
            paymentUrl.searchParams.set('prefilled_promo_code', promoCodeToUse);
            
            return paymentUrl.toString();
        } catch (error) {
            // If URL parsing fails, try simple string manipulation
            console.warn('Failed to parse Stripe payment link, using simple append:', error);
            
            // Remove existing prefilled_promo_code, then add new one
            let modifiedLink = originalLink;
            
            // Remove existing prefilled_promo_code parameter
            modifiedLink = modifiedLink.replace(/[?&]prefilled_promo_code=[^&]*/g, '');
            
            // Add new prefilled_promo_code
            const separator = modifiedLink.includes('?') ? '&' : '?';
            modifiedLink = `${modifiedLink}${separator}prefilled_promo_code=${encodeURIComponent(promoCodeToUse)}`;
            
            return modifiedLink;
        }
    };

    // Utility function to sort plans by index
    const sortPlansByIndex = (plans) => {
        if (!plans || plans.length === 0) return [];
        
        const plansWithIndex = plans.filter(plan => plan.index !== null && plan.index !== undefined);
        const plansWithoutIndex = plans.filter(plan => !plan.index || isNaN(parseInt(plan.index, 10)));
        
        const sorted = plansWithIndex.sort((a, b) => {
            const indexA = parseInt(a.index, 10);
            const indexB = parseInt(b.index, 10);
            if (isNaN(indexA)) return 1;
            if (isNaN(indexB)) return -1;
            return indexA - indexB;
        });
        
        return [...sorted, ...plansWithoutIndex];
    };

    // Get all plans that have landing: true and active: true
    // Combine subscriptions and oneTimeDeals only (exclude oneTimePurchases - add-on credits)
    const allPricingPlans = [...(plans.subscriptions || []), ...(plans.oneTimeDeals || [])];
    const activeLandingPlans = allPricingPlans.filter(plan => 
        plan && 
        plan.landing === true && 
        plan.active === true
    );
    
    // Initialize subscription type based on available plans
    const subscriptions = activeLandingPlans.filter(plan => 
        plan.type === 'weekly' || plan.type === 'monthly' || plan.type === 'yearly'
    );
    const oneTimeDeals = activeLandingPlans.filter(plan => 
        plan.type === 'one_time_deal' || plan.type === 'yearly_one_time'
    );
    
    const hasWeekly = subscriptions.some(plan => plan.type === 'weekly');
    const hasMonthly = subscriptions.some(plan => plan.type === 'monthly');
    const hasYearly = subscriptions.some(plan => plan.type === 'yearly');
    
    // Initialize subscription type based on available plans
    useEffect(() => {
        const availableTypesCount = [hasWeekly, hasMonthly, hasYearly, oneTimeDeals.length > 0].filter(Boolean).length;
        
        // Priority order: monthly > yearly > weekly > one-time
        if (availableTypesCount >= 2) {
            // Multiple types - set to first in priority order
            if (hasMonthly) {
                setSubscriptionType('monthly');
                setShowOneTimeDeals(false);
            } else if (hasYearly) {
                setSubscriptionType('yearly');
                setShowOneTimeDeals(false);
            } else if (hasWeekly) {
                setSubscriptionType('weekly');
                setShowOneTimeDeals(false);
            } else if (oneTimeDeals.length > 0) {
                setShowOneTimeDeals(true);
            }
        } else {
            // Only one type - set accordingly (toggle won't show)
            if (hasMonthly) {
                setSubscriptionType('monthly');
            } else if (hasYearly) {
                setSubscriptionType('yearly');
            } else if (hasWeekly) {
                setSubscriptionType('weekly');
            } else if (oneTimeDeals.length > 0) {
                setShowOneTimeDeals(true);
            }
        }
    }, [hasYearly, hasMonthly, hasWeekly, oneTimeDeals.length]);
    
    // Filter plans based on toggle selection
    let filteredPlans = [];
    if (showOneTimeDeals) {
        filteredPlans = oneTimeDeals;
    } else {
        filteredPlans = subscriptions.filter(plan => plan.type === subscriptionType);
    }
    
    const landingPlans = sortPlansByIndex(filteredPlans);



    const outerClasses = classNames(
        'testimonial section',
        topOuterDivider && 'has-top-divider',
        bottomOuterDivider && 'has-bottom-divider',
        hasBgColor && 'has-bg-color',
        invertColor && 'invert-color',
        className
    );

    const innerClasses = classNames(
        'testimonial-inner section-inner',
        topDivider && 'has-top-divider',
        bottomDivider && 'has-bottom-divider'
    );

    const tilesClasses = classNames('tiles-wrap', pushLeft && 'push-left');

    const sectionHeader = {
        title: 'Simple, Transparent Pricing',
        paragraph: 'Start free, upgrade when you need more. All plans include unlimited folders.'
    };

    const renderPlanPrice = (plan) => {
        const isSubscription = plan.type === 'weekly' || plan.type === 'monthly' || plan.type === 'yearly';
        const currencySymbol = plan.currency === 'eur' ? '€' :
            plan.currency === 'gbp' ? '£' :
                plan.currency === 'usd' ? '$' :
                    plan.currency?.toUpperCase() || '$';
        
        // Determine the suffix for the price and calculate display price
        let priceSuffix = '';
        let displayPrice = plan.price;
        let displayAnchorPrice = plan.anchorPrice;
        
        // For free plans (price === 0), don't show suffix so it can be displayed in both yearly and monthly sections
        const isFreePlan = !plan.price || plan.price === 0;
        
        if (isSubscription && !isFreePlan) {
            if (plan.type === 'weekly') {
                priceSuffix = '/wk';
            } else if (plan.type === 'monthly') {
                priceSuffix = '/mo';
            } else if (plan.type === 'yearly') {
                priceSuffix = '/mo'; // Show monthly equivalent for yearly plans
                // For yearly plans, plan.price is the yearly price, divide by 12 for monthly equivalent
                displayPrice = Math.trunc((plan.price / 12) * 100) / 100;
                if (plan.anchorPrice && parseFloat(plan.anchorPrice) > 0) {
                    displayAnchorPrice = Math.trunc((plan.anchorPrice / 12) * 100) / 100;
                }
            }
        }
        
        return (
            <>
                <div style={{ fontSize: 28, fontWeight: 'bold', color: PRIMARY_COLOR, lineHeight: '1' }}>
                    {currencySymbol}{displayPrice}{priceSuffix}
                    {displayAnchorPrice != null && 
                     displayAnchorPrice !== '' &&
                     displayAnchorPrice !== '0' &&
                     displayAnchorPrice !== 0 &&
                     !isNaN(parseFloat(displayAnchorPrice)) &&
                     parseFloat(displayAnchorPrice) > 0 && (
                        <span style={{ 
                            fontSize: 14, 
                            textDecoration: 'line-through', 
                            marginLeft: 8, 
                            color: RED,
                            fontWeight: 'normal'
                        }}>
                            {currencySymbol}{displayAnchorPrice}{priceSuffix}
                        </span>
                    )}
                </div>
                <div style={{ 
                    fontSize: 14,
                    opacity: 0.8,
                    fontWeight: '500',
                    marginTop: '8px',
                    minHeight: '20px' // Use minHeight instead of fixed height for flexibility
                }}>
                    {(!plan.price || plan.price === 0) ? (
                        ''
                    ) : isSubscription && plan.type === 'yearly' ? (
                        <span>
                            Billed as {currencySymbol}{Math.trunc(plan.price * 100) / 100}/year
                        </span>
                    ) : isSubscription ? (
                        plan.type === 'monthly' ? 'Billed monthly' :
                        plan.type === 'weekly' ? 'Billed weekly' :
                        `per ${plan.type}`
                    ) : (
                        'one time'
                    )}
                </div>
            </>
        );
    };

    const renderLandingFeatures = (plan) => {
        let features = [];

        if (plan.landingDescription) {
            try {
                features = JSON.parse(plan.landingDescription);
            } catch (e) {
                console.error('Invalid landingDescription format:', e);
            }
        }

        if (plan.featureLimits?.slackConnections !== undefined && plan.featureLimits?.slackConnections !== null) {
            const slackConnections = Number(plan.featureLimits.slackConnections);
            features.push({
                title:
                    slackConnections <= 0
                        ? 'Slack integration not included'
                        : slackConnections === 1
                            ? '1 Slack workspace'
                            : `${slackConnections} Slack workspaces`,
                description:
                    slackConnections <= 0
                        ? 'Upgrade to connect Slack workspaces.'
                        : 'Save notes and files directly from Slack into Luckynote.'
            });
        }

        if (features.length === 0) return null;

        return features.map((feature, index) => (
            <div key={index} style={{ flexDirection: 'row', display: 'flex', alignItems: 'flex-start', marginBottom: 16 }}>
                <div style={{ marginRight: 14, marginTop: 4 }}>
                    <div style={{
                        backgroundColor: GREEN, 
                        width: 20, 
                        height: 20, 
                        borderRadius: 20,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <FiCheck size={16} color={WHITE} />
                    </div>
                </div>
                <div style={{ fontSize: 16, lineHeight: '30px', flex: 1, fontWeight: '500' }}>
                    {feature.title}
                    <div style={{ fontSize: 14, lineHeight: '20px', color: PRIMARY_COLOR, fontWeight: 'normal' }}>
                        {feature.description}
                    </div>
                </div>
            </div>
        ));
    };

    const renderSubscriptionToggle = () => {
        // Check if we have available weekly, monthly, and yearly subscriptions with landing: true
        const availableWeeklyPlans = subscriptions.filter(plan => plan.type === 'weekly' && plan.active === true && plan.landing === true);
        const availableMonthlyPlans = subscriptions.filter(plan => plan.type === 'monthly' && plan.active === true && plan.landing === true);
        const availableYearlyPlans = subscriptions.filter(plan => plan.type === 'yearly' && plan.active === true && plan.landing === true);
        
        const hasWeekly = availableWeeklyPlans.length > 0;
        const hasMonthly = availableMonthlyPlans.length > 0;
        const hasYearly = availableYearlyPlans.length > 0;
        const hasOneTimeDeals = oneTimeDeals.length > 0;
        
        // Count how many types we have total (subscriptions + one-time)
        const availableTypesCount = [hasWeekly, hasMonthly, hasYearly, hasOneTimeDeals].filter(Boolean).length;
        
        // Only show toggle if we have at least 2 types available
        if (availableTypesCount < 2) {
            return null;
        }

        // Savings from paying yearly vs monthly, based on real plan prices
        const yearlySaving = (() => {
            if (!hasYearly || !hasMonthly) return null;
            const monthly = availableMonthlyPlans.find(p => parseFloat(p.price) > 0);
            const yearly = availableYearlyPlans.find(p => parseFloat(p.price) > 0);
            if (!monthly || !yearly) return null;
            const mo = parseFloat(monthly.price);
            const yr = parseFloat(yearly.price) / 12;
            if (!mo || mo <= 0) return null;
            return Math.round((1 - yr / mo) * 100);
        })();

        return (
            <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                marginBottom: 48,
                marginTop: yearlySaving > 0 ? 52 : -40
            }}>
                <div style={{ position: 'relative' }}>
                    {/* Save callout — handwritten style above the toggle */}
                    {yearlySaving > 0 && (
                        <div style={{
                            position: 'absolute', top: '-46px', left: '6px',
                            display: 'flex', alignItems: 'flex-end', gap: '2px',
                            pointerEvents: 'none', userSelect: 'none'
                        }}>
                            <span style={{
                                fontFamily: "'Caveat', 'Segoe Print', 'Bradley Hand', cursive",
                                fontSize: '26px', color: 'rgba(0,0,0,.45)', lineHeight: '1',
                                whiteSpace: 'nowrap', display: 'block', marginBottom: '4px'
                            }}>
                                Save {yearlySaving}%
                            </span>
                            {/* Curved arrow pointing down-right toward the toggle */}
                            <svg viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg"
                                width="44" height="36" style={{ transform: 'scaleX(-1)', flexShrink: 0 }}>
                                <path d="M18.7 1.2C16 21 21.3 39.3 34.2 43.4"
                                    stroke="rgba(0,0,0,.35)" strokeWidth="2" strokeLinecap="round" />
                                <path d="m36.3 38.8-.2.6c0 .2.1.5.4.8.4.8.8 1.7 1 2.6.4 1 .6 2 .7 2.8v1.8l-2 .1a18 18 0 0 1-4.8-.8 3 3 0 0 0-.8-.2c-.2 0-.4.1-.5.3a1 1 0 0 0-.3.7c0 .3.1.5.4.7l.3.2.7.3a16.9 16.9 0 0 0 3.6.6 29.9 29.9 0 0 0 4 0h.6a1.5 1.5 0 0 0 .5-.6l.2-.6a13.3 13.3 0 0 0-.1-4 17 17 0 0 0-1.9-5c0-.2-.2-.3-.3-.4a1 1 0 0 0-.7-.3c-.3 0-.5.1-.8.4Z"
                                    fill="rgba(0,0,0,.35)" />
                            </svg>
                        </div>
                    )}

                {/* Segmented Control - only show segments for available plan types */}
                <div style={{
                    display: 'flex',
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    borderRadius: 8,
                    padding: 4,
                    gap: 4,
                    width: 'fit-content'
                }}>
                    {hasYearly && (
                        <div
                            onClick={() => {
                                setSubscriptionType('yearly');
                                setShowOneTimeDeals(false);
                            }}
                            style={{
                                padding: '8px 24px',
                                borderRadius: 6,
                                backgroundColor: subscriptionType === 'yearly' && !showOneTimeDeals ? 'rgba(0, 0, 0, 0.2)' : 'transparent',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                userSelect: 'none'
                            }}
                        >
                            <span style={{
                                fontSize: 16,
                                fontWeight: subscriptionType === 'yearly' && !showOneTimeDeals ? '600' : '400',
                                color: PRIMARY_COLOR,
                                transition: 'all 0.2s ease'
                            }}>
                                Yearly
                            </span>
                        </div>
                    )}
                    
                    {hasMonthly && (
                        <div
                            onClick={() => {
                                setSubscriptionType('monthly');
                                setShowOneTimeDeals(false);
                            }}
                            style={{
                                padding: '8px 24px',
                                borderRadius: 6,
                                backgroundColor: subscriptionType === 'monthly' && !showOneTimeDeals ? 'rgba(0, 0, 0, 0.2)' : 'transparent',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                userSelect: 'none'
                            }}
                        >
                            <span style={{
                                fontSize: 16,
                                fontWeight: subscriptionType === 'monthly' && !showOneTimeDeals ? '600' : '400',
                                color: PRIMARY_COLOR,
                                transition: 'all 0.2s ease'
                            }}>
                                Monthly
                            </span>
                        </div>
                    )}
                    
                    {hasWeekly && (
                        <div
                            onClick={() => {
                                setSubscriptionType('weekly');
                                setShowOneTimeDeals(false);
                            }}
                            style={{
                                padding: '8px 24px',
                                borderRadius: 6,
                                backgroundColor: subscriptionType === 'weekly' && !showOneTimeDeals ? 'rgba(0, 0, 0, 0.2)' : 'transparent',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                userSelect: 'none'
                            }}
                        >
                            <span style={{
                                fontSize: 16,
                                fontWeight: subscriptionType === 'weekly' && !showOneTimeDeals ? '600' : '400',
                                color: PRIMARY_COLOR,
                                transition: 'all 0.2s ease'
                            }}>
                                Weekly
                            </span>
                        </div>
                    )}
                    
                    {hasOneTimeDeals && (
                        <div
                            onClick={() => {
                                setShowOneTimeDeals(true);
                            }}
                            style={{
                                padding: '8px 24px',
                                borderRadius: 6,
                                backgroundColor: showOneTimeDeals ? 'rgba(0, 0, 0, 0.2)' : 'transparent',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                userSelect: 'none'
                            }}
                        >
                            <span style={{
                                fontSize: 16,
                                fontWeight: showOneTimeDeals ? '600' : '400',
                                color: PRIMARY_COLOR,
                                transition: 'all 0.2s ease'
                            }}>
                                One-Time
                            </span>
                        </div>
                    )}
                </div>
                </div>
            </div>
        );
    };

    const renderSkeletonLoader = () => {
        // Render 3 skeleton cards to match the grid layout
        return (
            <section {...props} className={outerClasses} id="pricing">
                <div className="container">
                    <div className={innerClasses}>
                        <SectionHeader data={sectionHeader} className="center-content" />
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                            {[1, 2, 3].map((index) => (
                                <div key={index} style={{
                                    background: 'white',
                                    padding: '32px',
                                    borderRadius: '16px',
                                    border: '1px solid rgba(20, 17, 15, 0.08)',
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    minHeight: '100%'
                                }}>
                                    {/* Skeleton Plan Name */}
                                    <div style={{
                                        height: '28px',
                                        width: '60%',
                                        backgroundColor: '#e0e0e0',
                                        borderRadius: '4px',
                                        margin: '0 auto 12px',
                                        animation: 'pulse 1.5s ease-in-out infinite'
                                    }} />
                                    
                                    {/* Skeleton Price */}
                                    <div style={{
                                        height: '40px',
                                        width: '50%',
                                        backgroundColor: '#e0e0e0',
                                        borderRadius: '4px',
                                        margin: '0 auto 8px',
                                        animation: 'pulse 1.5s ease-in-out infinite',
                                        animationDelay: '0.1s'
                                    }} />
                                    
                                    {/* Skeleton Billing Period */}
                                    <div style={{
                                        height: '20px',
                                        width: '40%',
                                        backgroundColor: '#e0e0e0',
                                        borderRadius: '4px',
                                        margin: '0 auto 20px',
                                        animation: 'pulse 1.5s ease-in-out infinite',
                                        animationDelay: '0.2s'
                                    }} />
                                    
                                    {/* Skeleton Features */}
                                    {[1, 2, 3].map((featureIndex) => (
                                        <div key={featureIndex} style={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            marginBottom: 16,
                                            gap: 14
                                        }}>
                                            <div style={{
                                                width: 20,
                                                height: 20,
                                                backgroundColor: '#e0e0e0',
                                                borderRadius: '50%',
                                                flexShrink: 0,
                                                animation: 'pulse 1.5s ease-in-out infinite',
                                                animationDelay: `${0.3 + featureIndex * 0.1}s`
                                            }} />
                                            <div style={{ flex: 1 }}>
                                                <div style={{
                                                    height: '20px',
                                                    width: '80%',
                                                    backgroundColor: '#e0e0e0',
                                                    borderRadius: '4px',
                                                    marginBottom: 8,
                                                    animation: 'pulse 1.5s ease-in-out infinite',
                                                    animationDelay: `${0.3 + featureIndex * 0.1}s`
                                                }} />
                                                <div style={{
                                                    height: '16px',
                                                    width: '100%',
                                                    backgroundColor: '#e0e0e0',
                                                    borderRadius: '4px',
                                                    animation: 'pulse 1.5s ease-in-out infinite',
                                                    animationDelay: `${0.3 + featureIndex * 0.1}s`
                                                }} />
                                            </div>
                                        </div>
                                    ))}
                                    
                                    {/* Skeleton Button */}
                                    <div style={{
                                        marginTop: 'auto',
                                        paddingTop: '16px'
                                    }}>
                                        <div style={{
                                            height: '50px',
                                            width: '100%',
                                            backgroundColor: '#e0e0e0',
                                            borderRadius: '8px',
                                            animation: 'pulse 1.5s ease-in-out infinite',
                                            animationDelay: '0.6s'
                                        }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <style>{`
                    @keyframes pulse {
                        0%, 100% {
                            opacity: 1;
                        }
                        50% {
                            opacity: 0.5;
                        }
                    }
                `}</style>
            </section>
        );
    };

    if (loading) {
        return renderSkeletonLoader();
    }

    // ── Single-tier detection ──────────────────────────────────────────────────
    // Separate free (price === 0) from paid subscriptions so the free plan
    // doesn't count as a tier — it's rendered as a companion column instead.
    const freeSubscriptions = subscriptions.filter(p => !p.price || parseFloat(p.price) === 0);
    const paidSubscriptions = subscriptions.filter(p => p.price && parseFloat(p.price) > 0);

    // planType can be null in the DB — fall back to plan name so e.g. "Plus"
    // monthly + yearly still group as a single tier
    const uniquePaidPlanTypes = [...new Set(paidSubscriptions.map(p => p.planType || p.name).filter(Boolean))];
    const isSingleTier = uniquePaidPlanTypes.length === 1;

    if (isSingleTier) {
        const freePlan    = freeSubscriptions[0] || null;
        const tierMonthly = paidSubscriptions.find(p => p.type === 'monthly') || null;
        const tierYearly  = paidSubscriptions.find(p => p.type === 'yearly')  || null;

        return (
            <section {...props} className={outerClasses} id="pricing">
                <div className="container">
                    <div className={innerClasses}>
                        <SectionHeader data={sectionHeader} className="center-content" />
                        <SinglePlanPricing
                            freePlan={freePlan}
                            monthlyPlan={tierMonthly}
                            yearlyPlan={tierYearly}
                            getPaymentLinkWithPromo={getPaymentLinkWithPromo}
                        />
                    </div>
                </div>
            </section>
        );
    }
    // ─────────────────────────────────────────────────────────────────────────

    return (
        <section {...props} className={outerClasses} id="pricing">
            <div className="container">
                <div className={innerClasses}>
                    <SectionHeader data={sectionHeader} className="center-content" />
                    {renderSubscriptionToggle()}
                    <div style={{ 
                        display: landingPlans.length === 1 ? 'flex' : 'grid', 
                        gridTemplateColumns: landingPlans.length === 1 ? 'none' : 'repeat(auto-fit, minmax(300px, 1fr))', 
                        gap: '24px',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        {landingPlans.map((plan, index) => {
                            const showLimitedOffer = plan.limitedOffer?.isActive;
                            
                            return (
                            <div key={plan.id} style={{
                                background: 'white',
                                padding: '32px',
                                borderRadius: '16px',
                                border: '1px solid rgba(20, 17, 15, 0.08)',
                                position: 'relative',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: '100%',
                                overflow: 'visible',
                                maxWidth: landingPlans.length === 1 ? '350px' : 'none',
                                width: landingPlans.length === 1 ? '100%' : 'auto'
                            }}>
                                {/* Plan tag (e.g., "Most popular") - positioned at top edge, centered */}
                                {plan.tag && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '-12px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        zIndex: 10
                                    }}>
                                        <span style={{
                                            fontSize: '13px',
                                            fontWeight: '600',
                                            color: '#6F5A1F',
                                            backgroundColor: '#FFF6DA',
                                            border: '1px solid rgba(250, 230, 164, 1)',
                                            padding: '8px 16px',
                                            borderRadius: '20px',
                                            display: 'inline-block',
                                            boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)',
                                            textTransform: 'none'
                                        }}>
                                            {plan.tag}
                                        </span>
                                    </div>
                                )}
                                
                                {/* Content wrapper */}
                                <div style={{
                                    background: 'transparent',
                                    borderRadius: '0px',
                                    padding: '0px',
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative'
                                }}>
                                {/* Limited offer banner - positioned above card */}
                                {showLimitedOffer && (
                                    <LimitedOfferBanner
                                        text={plan.limitedOffer?.text}
                                        endDate={plan.limitedOffer?.endDate}
                                        position="top"
                                        showTimer={true}
                                    />
                                )}
                                
                                {/* Plan name - always display the actual plan name from database */}
                                <h3 style={{ 
                                    margin: '0 0 12px',
                                    fontSize: '24px',
                                    fontWeight: '600',
                                    textAlign: 'center'
                                }}>
                                    {plan.name}
                                </h3>
                                
                                {/* Price with payment type from backend */}
                                <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                                    {renderPlanPrice(plan)}
                                </div>
                                
                                {/* Features from database */}
                                <div style={{ marginBottom: '20px', textAlign: 'left', flex: 1 }}>
                                    {renderLandingFeatures(plan)}
                                </div>
                                
                                {/* CTA Button */}
                                <div style={{ marginTop: 'auto', paddingTop: '16px' }}>
                                        {plan.stripePaymentLink ? (
                                            <a
                                                href={getPaymentLinkWithPromo(plan.stripePaymentLink)}
                                                style={{
                                            display: 'block',
                                                    width: '100%',
                                            padding: '16px 24px',
                                            backgroundColor: GREEN,
                                            color: 'white',
                                            textDecoration: 'none',
                                            textAlign: 'center',
                                            borderRadius: '8px',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            border: 'none',
                                            transition: 'all 0.2s ease',
                                            boxShadow: '0 4px 12px rgba(0, 137, 111, 0.3)'
                                                }}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                        onMouseOver={(e) => {
                                            e.target.style.backgroundColor = '#007862';
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 6px 20px rgba(0, 137, 111, 0.4)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.target.style.backgroundColor = GREEN;
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = '0 4px 12px rgba(0, 137, 111, 0.3)';
                                        }}
                                    >
                                        Get {plan.name}
                                            </a>
                                        ) : (
                                            <Link
                                                to="/register"
                                                onClick={redirectToMobileStore}
                                                style={{
                                            display: 'block',
                                                    width: '100%',
                                            padding: '16px 24px',
                                            backgroundColor: GREEN,
                                            color: 'white',
                                            textDecoration: 'none',
                                            textAlign: 'center',
                                            borderRadius: '8px',
                                            fontSize: '16px',
                                            fontWeight: '600',
                                            border: 'none',
                                            transition: 'all 0.2s ease',
                                            boxShadow: '0 4px 12px rgba(0, 137, 111, 0.3)'
                                        }}
                                        onMouseOver={(e) => {
                                            e.target.style.backgroundColor = '#007862';
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 6px 20px rgba(0, 137, 111, 0.4)';
                                        }}
                                        onMouseOut={(e) => {
                                            e.target.style.backgroundColor = GREEN;
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = '0 4px 12px rgba(0, 137, 111, 0.3)';
                                                }}
                                            >
                                                Start free
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

Pricing.propTypes = propTypes;
Pricing.defaultProps = defaultProps;

export default Pricing;
